import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';
import { UserPlus, Copy, Check, Gift, Users, Crown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '@/hooks/usePageTitle';
import clsx from 'clsx';

export default function ReferralPage() {
    const { t } = useTranslation();
    usePageTitle('referral.title');
    const [copied, setCopied] = useState(false);

    // Fetch user data with referral info
    const { data, isLoading } = useQuery({
        queryKey: ['user-me'],
        queryFn: () => api.getMe(),
    });

    const userInfo = data?.user as any;
    const referralCode = userInfo?.referral_code || userInfo?.id?.slice(0, 8) || 'loading';
    const referralCount = userInfo?.referral_count || 0;
    const referralLink = `${window.location.origin}/register?ref=${referralCode}`;

    const copyToClipboard = async () => {
        await navigator.clipboard.writeText(referralLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const progressToNextReward = referralCount % 5;
    const totalRewardsEarned = Math.floor(referralCount / 5);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full" />
            </div>
        );
    }

    return (
        <div className="p-6 max-w-3xl mx-auto">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-neutral-800 dark:text-neutral-100">
                    {t('referral.title', 'Mời bạn bè')}
                </h1>
                <p className="text-neutral-500 dark:text-neutral-400">
                    {t('referral.subtitle', 'Mời 5 bạn bè = 1 tháng PRO miễn phí!')}
                </p>
            </div>

            {/* Referral Link Card */}
            <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-6 mb-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-primary-100 dark:bg-primary-600/20 rounded-xl flex items-center justify-center">
                        <UserPlus className="w-6 h-6 text-primary-600" />
                    </div>
                    <div>
                        <h2 className="font-semibold text-neutral-800 dark:text-neutral-100">
                            {t('referral.yourLink', 'Link giới thiệu của bạn')}
                        </h2>
                        <p className="text-sm text-neutral-500">
                            {t('referral.shareLink', 'Chia sẻ link này với bạn bè')}
                        </p>
                    </div>
                </div>

                <div className="flex gap-2">
                    <input
                        type="text"
                        readOnly
                        value={referralLink}
                        className="input flex-1 bg-neutral-50 dark:bg-neutral-700"
                    />
                    <button
                        onClick={copyToClipboard}
                        className={clsx(
                            'btn-primary px-4 flex items-center gap-2',
                            copied && 'bg-green-500'
                        )}
                    >
                        {copied ? (
                            <>
                                <Check className="w-4 h-4" />
                                {t('common.copied', 'Đã copy!')}
                            </>
                        ) : (
                            <>
                                <Copy className="w-4 h-4" />
                                {t('common.copy', 'Copy')}
                            </>
                        )}
                    </button>
                </div>

                <div className="mt-4 p-3 bg-neutral-50 dark:bg-neutral-700 rounded-lg">
                    <p className="text-sm text-neutral-600 dark:text-neutral-300">
                        <strong>{t('referral.code', 'Mã giới thiệu')}:</strong> {referralCode}
                    </p>
                </div>
            </div>

            {/* Progress Card */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-6 mb-6 text-white">
                <div className="flex items-center gap-3 mb-4">
                    <Gift className="w-8 h-8" />
                    <div>
                        <h2 className="text-lg font-semibold">
                            {t('referral.progress', 'Tiến độ nhận thưởng')}
                        </h2>
                        <p className="text-amber-100 text-sm">
                            {5 - progressToNextReward} {t('referral.moreNeeded', 'người nữa để nhận 1 tháng PRO')}
                        </p>
                    </div>
                </div>

                {/* Progress bar */}
                <div className="bg-white/30 rounded-full h-4 mb-3">
                    <div
                        className="bg-white rounded-full h-4 transition-all"
                        style={{ width: `${(progressToNextReward / 5) * 100}%` }}
                    />
                </div>

                <div className="flex justify-between text-sm">
                    <span>{progressToNextReward}/5 {t('referral.referrals', 'người đã mời')}</span>
                    <span className="flex items-center gap-1">
                        <Crown className="w-4 h-4" />
                        {totalRewardsEarned} {t('referral.monthsEarned', 'tháng PRO đã nhận')}
                    </span>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4">
                <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-5">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 dark:bg-green-600/20 rounded-lg flex items-center justify-center">
                            <Users className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-neutral-800 dark:text-neutral-100">{referralCount}</p>
                            <p className="text-sm text-neutral-500">{t('referral.totalInvited', 'Người đã mời')}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-5">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-100 dark:bg-amber-600/20 rounded-lg flex items-center justify-center">
                            <Crown className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-neutral-800 dark:text-neutral-100">{totalRewardsEarned}</p>
                            <p className="text-sm text-neutral-500">{t('referral.proMonths', 'Tháng PRO miễn phí')}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* How it works */}
            <div className="mt-6 bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-6">
                <h3 className="font-semibold text-neutral-800 dark:text-neutral-100 mb-4">
                    {t('referral.howItWorks', 'Cách hoạt động')}
                </h3>
                <ol className="space-y-3 text-sm text-neutral-600 dark:text-neutral-400">
                    <li className="flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-primary-100 dark:bg-primary-600/20 text-primary-600 rounded-full flex items-center justify-center font-medium text-xs">1</span>
                        {t('referral.step1', 'Chia sẻ link giới thiệu của bạn cho bạn bè')}
                    </li>
                    <li className="flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-primary-100 dark:bg-primary-600/20 text-primary-600 rounded-full flex items-center justify-center font-medium text-xs">2</span>
                        {t('referral.step2', 'Bạn bè đăng ký qua link của bạn')}
                    </li>
                    <li className="flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-primary-100 dark:bg-primary-600/20 text-primary-600 rounded-full flex items-center justify-center font-medium text-xs">3</span>
                        {t('referral.step3', 'Mỗi 5 người đăng ký = 1 tháng PRO miễn phí!')}
                    </li>
                </ol>
            </div>
        </div>
    );
}
